
/*
**************************************************************************
Title:          AI Chatbot Framework connect service
Created By:     Pankaj Arora
Created on:     05/04/2018
Description:    This service connects AI Chatbot to backend Framework
**************************************************************************
*/
import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { GetUrlParameters } from '../../utils';
import { throwError as observableThrowError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

interface ConversationContext {
  sessionId: string;
  userId: number;
  clientId: string;
  nluAttributes: any;
  appversion: string;
  subChannel: any;
}

@Injectable()
export class ChatAIConnectService {
  private conversationContext: ConversationContext;
  private urlParams = environment.urlParams;
  private urlParamsDform = environment.urlParamsDform;
  private ariaToken: string;

  constructor(
    private http: HttpClient
  ) { }

  // Send user interaction to AI Framework
  sendToChatAI(text: string, filedata?: any, attachments?: any) {
    let requestParams: any;
    let urlParameters: any;
    urlParameters = GetUrlParameters();

    const dateString = new Date().toString();
    const indexOfTimeZone = dateString.indexOf('GMT');
    const userTimeZone = dateString.substr(indexOfTimeZone, 20).split(' ')[0];

    // only happens the first time
    if (!this.conversationContext) {

      this.conversationContext = {
        sessionId: null,
        userId: urlParameters.userId,
        clientId: urlParameters.clientId,
        subChannel: urlParameters.subChannel,
        appversion: urlParameters.appversion,
        nluAttributes: {}
      };
    }
    // construct AI framework payload for texts
    // only send client Id the first time - this makes the app robust & difficult to intercept messages (not impossible)
    requestParams = {
      channel: {
        clientId: this.conversationContext.sessionId ? '' : this.conversationContext.clientId,
        subChannel: this.conversationContext.subChannel || '',
        sessionId: this.conversationContext.sessionId,
        appversion: this.conversationContext.appversion
      },
      user: {
        id: this.conversationContext.userId || ''
      },
      message: {
        text: text.toString() || '',
        timestamp: new Date().toJSON(),
        timezone: userTimeZone,
        type: this.conversationContext.sessionId ? 'message' : 'login'
      },
      actionParams: {},
      nluAttributes: this.conversationContext.nluAttributes || {},
      attachments: filedata ? [filedata] : attachments || []
    };

    if (filedata) {
      requestParams.message.type = 'attachment';
    }

    if (requestParams.message.type === 'login' && urlParameters.viewmyjobs) {
      requestParams.message.text = 'View my jobs';
    } else if (requestParams.message.type === 'login' && urlParameters.servicerequest && urlParameters.approvemyjobs) {
      requestParams.message.text = `notification approval required ${urlParameters.servicerequest}`;
    } else if (requestParams.message.type === 'login' && urlParameters.servicerequest) {
      requestParams.message.text = `I want update on my case ${urlParameters.servicerequest}`;
    }

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': environment.apikey
    });

    if (this.ariaToken) {
      headers = headers.set('Authorization', `bearer ${this.ariaToken}`);
    }
    if (urlParameters.local) {
      headers = headers.set('Authorization', `bearer ${environment.token}`);
    }

    return this.http
      .post(this.urlParams, requestParams, {headers})
      .pipe(
        map(this.handleAIResponse),
        catchError(this.handleErrorObservable));
  }

  postEnquiry(text, filedata?: any, attachments?: any) {
    let requestParams: any;

    const dateString = new Date().toString();
    const indexOfTimeZone = dateString.indexOf('GMT');
    const userTimeZone = dateString.substr(indexOfTimeZone, 20).split(' ')[0];

    const urlParameters = GetUrlParameters();
    let messageType = text ? 'enquiry' : 'login';

    if (!this.conversationContext) {
      this.conversationContext = {
        sessionId: null,
        userId: urlParameters.userId,
        clientId: urlParameters.clientId,
        subChannel: urlParameters.subChannel,
        appversion: urlParameters.appversion,
        nluAttributes: {}
      };
    }

    if (this.conversationContext.nluAttributes && this.conversationContext.nluAttributes.conversation) {
      messageType = 'message';
    }

    requestParams = {
      channel: {
        clientId: this.conversationContext.sessionId ? '' : this.conversationContext.clientId,
        subChannel: urlParameters.subChannel,
        sessionId: this.conversationContext.sessionId,
        appversion: this.conversationContext.appversion
      },
      user: {
        id: urlParameters.userId
      },
      message: {
        text: text.toString() || '',
        timestamp: new Date().toJSON(),
        timezone: userTimeZone,
        type: messageType
      },
      actionParams: {},
      nluAttributes: this.conversationContext.nluAttributes,
      attachments: filedata ? [filedata] : attachments || []
    };

    if (filedata) {
      requestParams.message.type = 'attachment';
    }

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': environment.apikey
    });

    if (this.ariaToken) {
      headers = headers.set('Authorization', `bearer ${this.ariaToken}`);
    }

    if (urlParameters.local) {
      const token = environment.token;
      headers = headers.set('Authorization', `bearer ${token}`);
    }

    const requestOptions = { headers };

    // if (requestParams.channel.clientId === 'e30e2354-8520-43cd-91ba-6ae3cc62bbe0' || this.conversationContext.clientId === 'e30e2354-8520-43cd-91ba-6ae3cc62bbe0') {
    //   this.urlParamsDform = 'https://esmedynamicforms-concept-dev.azurewebsites.net/api/getFormview?code=zcFMUYkqZTEBw4rTSS8svSaaZjLl25/5o3wzXvmdOnjgLwH8GN9Zlg==';
    // }

    return this.http
      .post(this.urlParamsDform, requestParams, requestOptions).pipe(
        map(this.handleAIResponse),
        catchError(this.handleErrorObservable));
  }

  clearConversationContext() {
    this.conversationContext = undefined;
  }

  // handle backend errors
  private handleErrorObservable(errorResponse: HttpErrorResponse | any) {
    return observableThrowError(errorResponse.error || errorResponse.message || errorResponse);
  }

  // handle backend response
  private handleAIResponse = (apiResponse: HttpErrorResponse | any) => {
    // keep session ID
    this.conversationContext.sessionId = apiResponse.sessionId;
    this.conversationContext.nluAttributes = apiResponse.nluAttributes;

    return apiResponse;
  }

  setAriaToken(token: string) {
    this.ariaToken = token;
  }

  getAriaToken() {
    return this.ariaToken;
  }

  getNluAttributes() {
    return this.conversationContext.nluAttributes;
  }

}
